import { useSyncExternalStore } from 'react';
import { request, setOnMaintenance, type MaintenanceNotice } from './client';

/**
 * Whether the till is switched off for maintenance, as a tiny store the request
 * layer can flip from outside React.
 *
 * Only a real 503 from a real call ever puts the screen up. The status endpoint
 * is asked just one question — "is it over yet?" — and never whether to block:
 * the server lets admins through during maintenance, so an admin signed in at
 * the till would be locked out by a screen that trusted the status alone.
 */

export interface MaintenanceState {
  active: boolean;
  message: string | null;
  endsAt: string | null;
  /**
   * Bumped each time maintenance ends. Screens that fetch on mount are keyed on
   * it, so they load afresh rather than showing what they had before the
   * server went away.
   */
  resumed: number;
}

let state: MaintenanceState = { active: false, message: null, endsAt: null, resumed: 0 };
const listeners = new Set<() => void>();

function setState(next: MaintenanceState) {
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMaintenance(): MaintenanceState {
  return useSyncExternalStore(subscribe, () => state);
}

function show(notice: MaintenanceNotice) {
  setState({
    ...state,
    active: true,
    message: notice.message ?? state.message,
    endsAt: notice.endsAt,
  });
}

// Registered on import rather than from an effect: a 503 on the very first
// request of the day must still find somebody listening.
setOnMaintenance(show);

interface AppMaintenance {
  enabled?: unknown;
  message?: unknown;
  endsAt?: unknown;
}

/**
 * Ask the server whether the till is back. Resolves true once it is and the
 * screen has come down; false while it is still closed, and false too when the
 * answer cannot be had or makes no sense — a flaky line must never be read as
 * "all clear" with the server still turning sales away.
 */
export async function checkMaintenanceOver(): Promise<boolean> {
  if (!state.active) return true;
  let pos: AppMaintenance | undefined;
  try {
    // skipRefresh: the endpoint is public, and a stray 401 from it must not
    // be taken for an expired session.
    const data = await request<Record<string, AppMaintenance> | null>('/app/maintenance', {
      skipRefresh: true,
    });
    pos = data && typeof data === 'object' ? data.pos : undefined;
  } catch {
    return false;
  }
  if (!pos || typeof pos !== 'object' || typeof pos.enabled !== 'boolean') return false;

  if (pos.enabled) {
    // Still closed — but the admin may have changed the wording or the time.
    setState({
      ...state,
      message: typeof pos.message === 'string' && pos.message.trim() ? pos.message : state.message,
      endsAt: typeof pos.endsAt === 'string' ? pos.endsAt : null,
    });
    return false;
  }

  if (state.active) {
    setState({ active: false, message: null, endsAt: null, resumed: state.resumed + 1 });
  }
  return true;
}
